import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { library } from "@fortawesome/fontawesome-svg-core";
import { login, logout, authError } from "@/store/authSlice";
import Header from "./components/Elements/Header";
import Preloader from "@/components/Elements/Preloader";
import {
  Dashboard,
  EventsResolving,
  Field,
  FieldPage,
  Exercises,
  Login,
  Register,
  SuperTrackMarkup,
  SuperTracks,
  TrackMatching,
  Intern,
  InternTrackMatching,
  Manager,
  Validation,
  Tasks,
  Orders,
  Videos,
  Summary,
  EventLight,
  Events,
  VerifyPositions,
  Cameras,
  PlaygroundFieldPage,
  CalibrateCameraPage,
} from "./pages";
import NotFound from "./components/Elements/NotFound";
import PrivateRoute from "./components/Elements/PrivateRoute";
import {
  DASHBOARD,
  FIELD_MARKUP,
  FIELD_PAGE,
  EXERCISES,
  LOGIN,
  NOT_FOUND,
  REGISTER,
  SUPER_TRACKS_MARKUP,
  SUPER_TRACK_MARKUP,
  TRACK_MATCHING,
  LABELER,
  INTERN,
  INTERN_CONFLICTS,
  INTERN_EVENTS,
  MANAGER,
  VALIDATION,
  TASKS,
  ORDERS,
  VIDEOS,
  SUMMARY,
  EVENT_LIGHT,
  EVENTS,
  VERIFY_POSITIONS,
  CAMERAS,
  PLAYGROUND_FIELD,
  CALIBRATE_CAMERA,
} from "./constants/routs.constants";
import { iconListBrand, iconListSolid } from "./core/font-awesome-icons";
import ErrorHandler from "./components/Elements/ErrorHandler";
import Labeler from "./pages/Labeler";
import "react-toastify/dist/ReactToastify.css";
import { authApi } from "./api";
import { catchErrors } from "./util/functions";

library.add(...iconListBrand, ...iconListSolid);

const App = () => {
  const { isAuth, error, user } = useSelector(({ auth }) => auth);
  const isHasToken = !!localStorage.getItem("access");
  const dispatch = useDispatch();
  const location = useLocation();
  const hideHeader = location.pathname.match(/\/markup\//g)?.length >= 2 && location.pathname.includes("/super-tracks/");

  useEffect(() => {
    const getUserStatus = async () => {
      try {
        const data = await authApi.getUser();

        dispatch(login(data));
        // dispatch(yandexAuthStatus(isYandexAuth))
      } catch (e) {
        dispatch(catchErrors(e, authError));
      }
    };

    if (isHasToken && !user?.username) {
      getUserStatus();
    }

    if (isHasToken && error?.status === 401) {
      dispatch(logout());
      alert.warning(
        "You were automatically logged out because the server rejected token authorization"
      );
    }
    if (isHasToken && error?.status === 403) {
      dispatch(logout());
      toast.warning(
        "Your account does not have access to the panel, contact your administrator."
      );
    }
  }, [dispatch, isAuth, isHasToken, error]);

  return isAuth || (!isAuth && error) || !isHasToken ? (
    <div className="wrapper">
      <ToastContainer position="bottom-right" autoClose={5000} />

      {!hideHeader && <Header />}
      <div className={`content tw-bg-[#f9f8f8]${hideHeader ? " content--no-header" : ""}`}>
        <ErrorHandler>
          <Routes>
            <Route
              exact
              path={INTERN}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route index element={<Intern />} />
              <Route
                path={INTERN_CONFLICTS}
                element={<InternTrackMatching />}
              />
              <Route path={INTERN_EVENTS} element={<EventsResolving />} />
            </Route>
            <Route
              exact
              path={LABELER}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route exact path={LABELER} element={<Labeler />} />
            </Route>
            <Route
              exact
              path={DASHBOARD}
              element={
                <PrivateRoute
                  authed={isHasToken}
                  user={user}
                  requireAdmin={true}
                  redirectTo={LABELER}
                />
              }
            >
              <Route exact path={DASHBOARD} element={<Dashboard />} />
            </Route>
            <Route
              exact
              path={VIDEOS}
              element={
                <PrivateRoute
                  authed={isHasToken}
                  user={user}
                  requireAdmin={true}
                  redirectTo={LABELER}
                />
              }
            >
              <Route exact path={VIDEOS} element={<Videos />} />
            </Route>
            <Route
              exact
              path={FIELD_MARKUP}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route exact path={FIELD_MARKUP} element={<Field />} />
            </Route>
            <Route
              exact
              path={FIELD_PAGE}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route exact path={FIELD_PAGE} element={<FieldPage />} />
            </Route>
            <Route
              exact
              path={TRACK_MATCHING}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route exact path={TRACK_MATCHING} element={<TrackMatching />} />
            </Route>
            <Route
              exact
              path={SUPER_TRACKS_MARKUP}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route
                exact
                path={SUPER_TRACKS_MARKUP}
                element={<SuperTracks />}
              />
            </Route>
            <Route
              exact
              path={SUPER_TRACK_MARKUP}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route
                exact
                path={SUPER_TRACK_MARKUP}
                element={<SuperTrackMarkup />}
              />
            </Route>
            <Route
              exact
              path={MANAGER}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={MANAGER} element={<Manager />} />
            </Route>
            <Route
              exact
              path={VALIDATION}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={VALIDATION} element={<Validation />} />
            </Route>
            <Route
              exact
              path={EVENTS}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={EVENTS} element={<Events />} />
            </Route>
            <Route
              exact
              path={EVENT_LIGHT}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={EVENT_LIGHT} element={<EventLight />} />
            </Route>
            <Route
              exact
              path={VERIFY_POSITIONS}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={VERIFY_POSITIONS} element={<VerifyPositions />} />
            </Route>
            <Route
              exact
              path={CALIBRATE_CAMERA}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={CALIBRATE_CAMERA} element={<CalibrateCameraPage />} />
            </Route>
            <Route
              exact
              path={TASKS}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={TASKS} element={<Tasks />} />
            </Route>
            <Route
              exact
              path={ORDERS}
              element={
                <PrivateRoute
                  authed={isHasToken}
                  user={user}
                  requireAdmin={true}
                  redirectTo={LABELER}
                />
              }
            >
              <Route exact path={ORDERS} element={<Orders />} />
            </Route>
            <Route
              exact
              path={CAMERAS}
              element={
                <PrivateRoute
                  authed={isHasToken}
                  user={user}
                  requireAdmin={true}
                  redirectTo={LABELER}
                />
              }
            >
              <Route exact path={CAMERAS} element={<Cameras />} />
            </Route>
            <Route
              exact
              path={PLAYGROUND_FIELD}
              element={
                <PrivateRoute
                  authed={isHasToken}
                  user={user}
                  requireAdmin={true}
                  redirectTo={LABELER}
                />
              }
            >
              <Route exact path={PLAYGROUND_FIELD} element={<PlaygroundFieldPage />} />
            </Route>
            <Route
              exact
              path={SUMMARY}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route path={SUMMARY} element={<Summary />} />
            </Route>
            <Route
              exact
              path={EXERCISES}
              element={<PrivateRoute authed={isHasToken} user={user} />}
            >
              <Route exact path={EXERCISES} element={<Exercises />} />
            </Route>
            <Route
              path={LOGIN}
              element={isAuth ? <Navigate to={DASHBOARD} /> : <Login />}
            />
            <Route
              path={REGISTER}
              element={isAuth ? <Navigate to={DASHBOARD} /> : <Register />}
            />
            <Route
              path={NOT_FOUND}
              element={!isAuth ? <Navigate to={LOGIN} /> : <NotFound />}
            />
            <Route path="" element={<Navigate to={LOGIN} />} />
          </Routes>
        </ErrorHandler>
      </div>
    </div>
  ) : (
    <Preloader />
  );
};

export default App;
